import type { MenuProps } from "antd";
import type { SiteInfo } from "@/components/UI/SitesMap/types";
import NotesTab from "@/views/shared/NotesTab";
import OverviewTab from "../tabs/OverviewTab";
import MilestonesTab from "../tabs/MilestonesTab";
import SubjectsTab from "../tabs/SubjectsTab";
import VisitsTab from "../tabs/VisitsTab";
import CRFCompletionTab from "../tabs/CRFCompletionTab";
import DiscrepanciesTab from "../tabs/DiscrepanciesTab";
import AdverseEventsTab from "../tabs/AdverseEventsTab";
import ContactsTab from "../tabs/ContactsTab";
import ProtocolDeviationsTab from "../tabs/ProtocolDeviationsTab";
import RiskTab from "../tabs/RiskTab";
import DocumentsTab from "../tabs/DocumentsTab";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";

/**
 * Tab menu for the site profile.
 *
 * Per-tab filtering is opt-in via SITE_TAB_GATING: without that flag every tab
 * renders exactly as before, so tenants that were never granted the individual
 * backing-data flags (clario, takeda, demo) keep all eleven tabs. With the flag
 * on, tabs whose backing data the trial does not have are hidden entirely
 * rather than showing an empty screen.
 *
 * Flag mapping: milestones -> site-activation-milestone (the timeline data
 * source), documents -> documents (TMF), monitoring-visits ->
 * site-monitoring-visits, crf-completion -> visits-activities, subjects/safety/
 * deviations/discrepancies follow their screen flags, risk -> risks OR
 * site-risk-lifecycle (each band inside the tab gates on its own flag).
 */
export const createSiteMenuItems = (
  isFlagEnabled: (flag: string) => boolean = () => false,
): MenuProps["items"] => {
  const groups = [
    {
      type: "group" as const,
      label: "SITE OVERVIEW",
      key: "group-site-overview",
      children: [
        { key: "overview", label: "Overview" },
        { key: "contacts", label: "Contacts" },
        {
          key: "milestones",
          label: "Timeline & Milestones",
          flag: FEATURE_FLAGS.SITE_ACTIVATION_MILESTONE,
        },
        {
          key: "subjects",
          label: "Subjects",
          flag: FEATURE_FLAGS.SUBJECTS_OVERVIEW,
        },
        { key: "documents", label: "Documents", flag: FEATURE_FLAGS.DOCUMENTS },
      ],
    },
    {
      type: "group" as const,
      label: "SITE MONITORING",
      key: "group-monitoring",
      children: [
        {
          key: "monitoring-visits",
          label: "Monitoring Visits",
          flag: FEATURE_FLAGS.SITE_MONITORING_VISITS,
        },
        {
          key: "adverse-events",
          label: "Safety",
          flag: FEATURE_FLAGS.ADVERSE_EVENTS,
        },
        {
          key: "protocol-deviations",
          label: "Protocol Deviations",
          flag: FEATURE_FLAGS.PROTOCOL_DEVIATIONS,
        },
      ],
    },
    {
      type: "group" as const,
      label: "SITE DATA",
      key: "group-data-integrity",
      children: [
        {
          key: "crf-completion",
          label: "Data Review",
          flag: FEATURE_FLAGS.VISITS_ACTIVITIES,
        },
        {
          key: "discrepancies",
          label: "Discrepancies",
          flag: FEATURE_FLAGS.DATA_DISCREPANCIES,
        },
      ],
    },
    {
      type: "group" as const,
      label: "RISK",
      key: "group-risk",
      // Any-of: RISKS owns the conduct-phase RBQM band, SITE_RISK_LIFECYCLE the
      // startup and close-out bands. Either alone is a useful tab, so the tab
      // shows when either is granted and each band gates itself inside.
      children: [
        {
          key: "risk",
          label: "Risk",
          anyFlags: [FEATURE_FLAGS.RISKS, FEATURE_FLAGS.SITE_RISK_LIFECYCLE],
        },
      ],
    },
    ...(isFlagEnabled(FEATURE_FLAGS.NOTES_HIDDEN)
      ? []
      : [
          {
            type: "group" as const,
            label: "NOTES",
            key: "group-notes",
            children: [{ key: "notes", label: "Notes" }],
          },
        ]),
  ];

  const gatingEnabled = isFlagEnabled(FEATURE_FLAGS.SITE_TAB_GATING);

  return groups
    .map((g) => ({
      ...g,
      children: g.children
        .filter((c: { flag?: string; anyFlags?: string[] }) => {
          if (!gatingEnabled) return true;
          if (c.anyFlags) return c.anyFlags.some(isFlagEnabled);
          return !c.flag || isFlagEnabled(c.flag);
        })
        .map((c) => ({ key: c.key, label: c.label })),
    }))
    .filter((g) => g.children.length > 0);
};

export const createContentMap = (
  siteId: string,
  siteInfo: SiteInfo | null,
  invKey?: string | null,
): Record<string, React.ReactNode> => ({
  overview: <OverviewTab siteInfo={siteInfo} />,
  contacts: <ContactsTab siteInfo={siteInfo} invKey={invKey} />,
  milestones: <MilestonesTab siteId={siteId} />,
  subjects: <SubjectsTab siteId={siteId} />,
  "monitoring-visits": <VisitsTab siteInfo={siteInfo} />,
  "crf-completion": <CRFCompletionTab siteId={siteId} />,
  discrepancies: <DiscrepanciesTab siteId={siteId} />,
  "adverse-events": <AdverseEventsTab siteId={siteId} />,
  "protocol-deviations": <ProtocolDeviationsTab siteId={siteId} />,
  risk: <RiskTab siteInfo={siteInfo} />,
  documents: <DocumentsTab />,
  notes: <NotesTab entityKey={`trial-site:${siteId}`} />,
});
